import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Plus, Trash2, Pencil, Check, X, ShoppingCart, Sparkles, Eraser, Users, LogOut, Loader2 } from 'lucide-react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import { apiFetch } from '@/hooks/useFetch';

const GROCERY_KEY = 'clearcomm-grocery';
const POLL_MS = 5000;

// Store-walk order (typical US supermarket layout)
export const GROCERY_SECTIONS = [
  { id: 'produce',       en: 'Produce',             es: 'Frutas y Verduras',     color: '#10B981' },
  { id: 'bakery',        en: 'Bakery',              es: 'Panadería',             color: '#F59E0B' },
  { id: 'deli',          en: 'Deli',                es: 'Charcutería',           color: '#F97316' },
  { id: 'meat',          en: 'Meat',                es: 'Carnes',                color: '#EF4444' },
  { id: 'dairy',         en: 'Dairy',               es: 'Lácteos',               color: '#3B82F6' },
  { id: 'refrigerated',  en: 'Refrigerated',        es: 'Refrigerados',          color: '#06B6D4' },
  { id: 'frozen',        en: 'Frozen',              es: 'Congelados',            color: '#60A5FA' },
  { id: 'canned',        en: 'Canned Items',        es: 'Enlatados',             color: '#8B5CF6' },
  { id: 'international', en: 'International Foods', es: 'Comida Internacional',  color: '#EC4899' },
  { id: 'plant-based',   en: 'Plant-Based',         es: 'A Base de Plantas',     color: '#84CC16' },
  { id: 'health',        en: 'Health Section',      es: 'Salud',                 color: '#14B8A6' },
  { id: 'general',       en: 'General Aisles',      es: 'Pasillos Generales',    color: '#6366F1' },
];

// Order matters — more specific sections are checked before broader ones
// (e.g. "almond milk" must hit plant-based before dairy's "milk",
// "ice cream" must hit frozen before dairy's "cream").
const SECTION_KEYWORDS = [
  ['plant-based', ['tofu', 'tempeh', 'seitan', 'vegan', 'plant-based', 'plant based', 'almond milk', 'oat milk', 'soy milk', 'coconut milk', 'cashew milk', 'coconut yogurt', 'veggie burger', 'meatless', 'vegano', 'leche de almendra', 'leche de avena', 'leche de soya']],
  ['frozen', ['frozen', 'ice cream', 'popsicle', 'waffle', 'fries', 'nugget', 'ice pop', 'sorbet', 'congelado', 'helado', 'paleta', 'nieve']],
  ['canned', ['canned', 'can of', 'soup', 'beans', 'tuna', 'chickpea', 'tomato sauce', 'tomato paste', 'broth', 'stock', 'enlatado', 'lata', 'sopa', 'frijol', 'atún', 'atun', 'garbanzo', 'caldo']],
  ['deli', ['deli', 'sliced ham', 'sliced turkey', 'salami', 'prosciutto', 'pastrami', 'bologna', 'cold cut', 'lunch meat', 'rotisserie', 'jamón', 'jamon', 'mortadela', 'fiambre']],
  ['international', ['soy sauce', 'sriracha', 'ramen', 'noodles', 'curry', 'kimchi', 'miso', 'tahini', 'taco shell', 'teriyaki', 'hoisin', 'fish sauce', 'masa', 'adobo', 'mole', 'wasabi', 'gochujang', 'pad thai', 'salsa de soya']],
  ['health', ['vitamin', 'medicine', 'tylenol', 'advil', 'ibuprofen', 'aspirin', 'shampoo', 'conditioner', 'soap', 'toothpaste', 'toothbrush', 'deodorant', 'lotion', 'sunscreen', 'band-aid', 'bandage', 'razor', 'floss', 'vitamina', 'medicina', 'jabón', 'jabon', 'champú', 'champu', 'pasta de dientes', 'desodorante', 'protector solar', 'curitas']],
  ['bakery', ['bread', 'bagel', 'muffin', 'croissant', 'baguette', 'tortillas', 'pita', 'donut', 'doughnut', 'cake', 'pie', 'pastry', 'bun', 'rolls', 'bolillo', 'pastel', 'dona', 'panque', 'pan dulce']],
  ['meat', ['chicken', 'beef', 'pork', 'steak', 'ground turkey', 'ground beef', 'sausage', 'bacon', 'lamb', 'fish', 'salmon', 'shrimp', 'tilapia', 'cod', 'crab', 'ribs', 'wings', 'hot dog', 'carne', 'pollo', 'res', 'cerdo', 'puerco', 'bistec', 'molida', 'pavo', 'salchicha', 'tocino', 'cordero', 'pescado', 'salmón', 'camarón', 'camaron', 'costillas', 'alitas']],
  ['dairy', ['milk', 'cheese', 'yogurt', 'yoghurt', 'butter', 'cream', 'egg', 'mozzarella', 'cheddar', 'parmesan', 'cottage', 'leche', 'queso', 'yogur', 'mantequilla', 'crema', 'huevo']],
  ['refrigerated', ['juice', 'hummus', 'guacamole', 'dough', 'pudding', 'jello', 'lunchable', 'dip', 'jugo', 'gelatina']],
  ['produce', ['apple', 'banana', 'orange', 'grape', 'berr', 'lettuce', 'spinach', 'kale', 'tomato', 'onion', 'garlic', 'potato', 'carrot', 'celery', 'broccoli', 'cucumber', 'pepper', 'avocado', 'lemon', 'lime', 'mango', 'melon', 'pineapple', 'peach', 'pear', 'plum', 'cilantro', 'parsley', 'mushroom', 'zucchini', 'squash', 'corn', 'cabbage', 'fruit', 'vegetable', 'manzana', 'plátano', 'platano', 'naranja', 'uva', 'fresa', 'lechuga', 'espinaca', 'tomate', 'cebolla', 'ajo', 'papa', 'zanahoria', 'apio', 'brócoli', 'brocoli', 'pepino', 'aguacate', 'limón', 'limon', 'sandía', 'sandia', 'piña', 'durazno', 'pera', 'champiñón', 'verdura', 'fruta']],
];

export const suggestSection = (text) => {
  const low = ` ${(text || '').toLowerCase().trim()} `;
  if (!low.trim()) return 'general';
  for (const [section, words] of SECTION_KEYWORDS) {
    if (words.some((w) => low.includes(w))) return section;
  }
  return 'general';
};

const sectionLabel = (sec, isSpanish) => {
  if (sec && sec.startsWith('custom:')) return sec.slice(7);
  const s = GROCERY_SECTIONS.find((x) => x.id === sec);
  return s ? (isSpanish ? s.es : s.en) : (isSpanish ? 'Pasillos Generales' : 'General Aisles');
};

const sectionColor = (sec) =>
  GROCERY_SECTIONS.find((x) => x.id === sec)?.color || '#9CA3AF';

export const GroceryList = ({ isSpanish = false, isLoggedIn = false, userId = null }) => {
  const [items, setItems] = useState([]);               // personal list
  const [sharedLists, setSharedLists] = useState([]);   // lists I belong to
  const [activeList, setActiveList] = useState('mine'); // 'mine' | list_id
  const [sharedData, setSharedData] = useState(null);   // {list, items} for active shared list
  const [newText, setNewText] = useState('');
  const [sectionChoice, setSectionChoice] = useState('auto');
  const [customName, setCustomName] = useState('');
  const [editingId, setEditingId] = useState(null);
  const [editText, setEditText] = useState('');
  const [editSection, setEditSection] = useState('general');
  const [shareOpen, setShareOpen] = useState(false);
  const [friends, setFriends] = useState(null);         // null = not loaded yet
  const [pickedFriends, setPickedFriends] = useState(new Set());
  const [shareName, setShareName] = useState('');
  const [creating, setCreating] = useState(false);
  const loaded = useRef(false);
  const synced = useRef(false);

  const isShared = activeList !== 'mine';
  const displayItems = isShared ? (sharedData?.items || []) : items;

  // ── Personal list persistence (unchanged behaviour) ──
  useEffect(() => {
    const saved = localStorage.getItem(GROCERY_KEY);
    if (saved) { try { setItems(JSON.parse(saved)); } catch { /* ignore */ } }
    loaded.current = true;
  }, []);

  useEffect(() => {
    if (isLoggedIn && !synced.current) {
      synced.current = true;
      (async () => {
        const res = await apiFetch('/api/grocery');
        if (res.data?.items?.length > 0) {
          setItems(res.data.items);
        } else {
          const local = JSON.parse(localStorage.getItem(GROCERY_KEY) || '[]');
          if (local.length > 0) {
            await apiFetch('/api/grocery/sync', { method: 'POST', body: { items: local } });
            const r2 = await apiFetch('/api/grocery');
            if (r2.data?.items) setItems(r2.data.items);
          }
        }
      })();
      fetchSharedLists();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoggedIn]);

  useEffect(() => {
    if (loaded.current) localStorage.setItem(GROCERY_KEY, JSON.stringify(items));
  }, [items]);

  // ── Shared lists ──
  const fetchSharedLists = useCallback(async () => {
    const { data } = await apiFetch('/api/grocery/shared');
    if (data?.lists) setSharedLists(data.lists);
  }, []);

  const loadSharedItems = useCallback(async (listId) => {
    const { data, error } = await apiFetch(`/api/grocery/shared/${listId}/items`);
    if (data) setSharedData(data);
    else if (error) {
      // Kicked / list deleted — bounce back to personal list.
      setActiveList('mine');
      setSharedData(null);
      fetchSharedLists();
    }
  }, [fetchSharedLists]);

  // Poll the active shared list so a partner's checkmarks appear live.
  useEffect(() => {
    if (!isShared || !isLoggedIn) return undefined;
    setSharedData(null);
    loadSharedItems(activeList);
    const t = setInterval(() => {
      if (!document.hidden) loadSharedItems(activeList);
    }, POLL_MS);
    return () => clearInterval(t);
  }, [activeList, isShared, isLoggedIn, loadSharedItems]);

  const acceptInvite = async (listId) => {
    const { error } = await apiFetch(`/api/grocery/shared/${listId}/accept`, { method: 'POST' });
    if (error) return toast.error(error);
    toast.success(isSpanish ? '¡Lista compartida aceptada!' : 'Shared list joined!');
    await fetchSharedLists();
    setActiveList(listId);
  };

  const declineInvite = async (listId) => {
    await apiFetch(`/api/grocery/shared/${listId}/decline`, { method: 'POST' });
    fetchSharedLists();
  };

  const leaveList = async (listId) => {
    if (!window.confirm(isSpanish
      ? '¿Salir de esta lista compartida? Si eres la última persona, la lista se eliminará.'
      : 'Leave this shared list? If you are the last person, the list will be deleted.')) return;
    const { error } = await apiFetch(`/api/grocery/shared/${listId}/leave`, { method: 'POST' });
    if (error) return toast.error(error);
    toast.info(isSpanish ? 'Saliste de la lista' : 'You left the list');
    setActiveList('mine');
    setSharedData(null);
    fetchSharedLists();
  };

  const openShareDialog = async () => {
    setShareOpen(true);
    if (friends === null) {
      const { data } = await apiFetch('/api/friends/list');
      setFriends(Array.isArray(data) ? data : []);
    }
  };

  const createSharedList = async () => {
    if (pickedFriends.size === 0) {
      return toast.error(isSpanish ? 'Elige al menos un amigo' : 'Pick at least one friend');
    }
    setCreating(true);
    const { data, error } = await apiFetch('/api/grocery/shared', {
      method: 'POST',
      body: { name: shareName.trim() || null, friend_ids: Array.from(pickedFriends) },
    });
    setCreating(false);
    if (error) return toast.error(error);
    toast.success(isSpanish
      ? '¡Lista compartida creada! Tus amigos verán la invitación en su pestaña de compras.'
      : 'Shared list created! Your friends will see the invite on their Grocery tab.');
    setShareOpen(false);
    setPickedFriends(new Set());
    setShareName('');
    await fetchSharedLists();
    if (data?.list_id) setActiveList(data.list_id);
  };

  // ── Suggestion + grouping (works on whichever list is active) ──
  const suggested = useMemo(() => suggestSection(newText), [newText]);
  const effectiveSection = sectionChoice === 'auto' ? suggested : sectionChoice;

  const customSections = useMemo(() => {
    const set = new Set(displayItems.map((i) => i.section).filter((s) => s && s.startsWith('custom:')));
    return [...set].sort((a, b) => a.localeCompare(b));
  }, [displayItems]);

  const grouped = useMemo(() => {
    const map = new Map();
    for (const it of displayItems) {
      const key = it.section || 'general';
      if (!map.has(key)) map.set(key, []);
      map.get(key).push(it);
    }
    const orderedKeys = [
      ...GROCERY_SECTIONS.map((s) => s.id).filter((id) => map.has(id)),
      ...[...map.keys()].filter((k) => k.startsWith('custom:')).sort((a, b) => a.localeCompare(b)),
      ...[...map.keys()].filter((k) => !k.startsWith('custom:') && !GROCERY_SECTIONS.some((s) => s.id === k)),
    ];
    return orderedKeys.map((k) => ({
      key: k,
      items: [...map.get(k)].sort(
        (a, b) => (a.completed === b.completed ? a.text.localeCompare(b.text) : a.completed ? 1 : -1)
      ),
    }));
  }, [displayItems]);

  const checkedCount = displayItems.filter((i) => i.completed).length;

  // ── CRUD (branches personal vs shared) ──
  const setDisplayItems = (updater) => {
    if (isShared) {
      setSharedData((prev) => prev ? { ...prev, items: updater(prev.items) } : prev);
    } else {
      setItems(updater);
    }
  };

  const addItem = (e) => {
    e.preventDefault();
    const text = newText.trim();
    if (!text) return;
    let section = effectiveSection;
    if (sectionChoice === '__custom__') {
      const name = customName.trim();
      if (!name) {
        toast.error(isSpanish ? 'Escribe el nombre de la sección' : 'Type the section name');
        return;
      }
      section = `custom:${name}`;
    }
    const item = {
      id: crypto.randomUUID(),
      text,
      section,
      completed: false,
      createdAt: new Date().toISOString(),
    };
    if (isShared) {
      apiFetch(`/api/grocery/shared/${activeList}/items`, {
        method: 'POST', body: { id: item.id, text: item.text, section: item.section },
      });
    } else if (isLoggedIn) {
      apiFetch('/api/grocery', { method: 'POST', body: { id: item.id, text: item.text, section: item.section } });
    }
    setDisplayItems((prev) => [item, ...prev]);
    setNewText('');
    setSectionChoice('auto');
    setCustomName('');
    toast.success(
      isSpanish
        ? `Agregado a ${sectionLabel(section, true)}`
        : `Added to ${sectionLabel(section, false)}`
    );
  };

  const toggleItem = (id) => {
    setDisplayItems((prev) =>
      prev.map((i) => {
        if (i.id !== id) return i;
        const next = { ...i, completed: !i.completed };
        if (isShared) {
          apiFetch(`/api/grocery/shared/${activeList}/items/${id}`, { method: 'PUT', body: { completed: next.completed } });
        } else if (isLoggedIn) {
          apiFetch(`/api/grocery/${id}`, { method: 'PUT', body: { completed: next.completed } });
        }
        return next;
      })
    );
  };

  const deleteItem = (id) => {
    if (isShared) {
      apiFetch(`/api/grocery/shared/${activeList}/items/${id}`, { method: 'DELETE' });
    } else if (isLoggedIn) {
      apiFetch(`/api/grocery/${id}`, { method: 'DELETE' });
    }
    setDisplayItems((prev) => prev.filter((i) => i.id !== id));
  };

  const startEdit = (item) => {
    setEditingId(item.id);
    setEditText(item.text);
    setEditSection(item.section || 'general');
  };

  const saveEdit = (id) => {
    const text = editText.trim();
    if (!text) return;
    if (isShared) {
      apiFetch(`/api/grocery/shared/${activeList}/items/${id}`, { method: 'PUT', body: { text, section: editSection } });
    } else if (isLoggedIn) {
      apiFetch(`/api/grocery/${id}`, { method: 'PUT', body: { text, section: editSection } });
    }
    setDisplayItems((prev) => prev.map((i) => (i.id === id ? { ...i, text, section: editSection } : i)));
    setEditingId(null);
  };

  const clearChecked = () => {
    if (isShared) {
      apiFetch(`/api/grocery/shared/${activeList}/clear-checked`, { method: 'POST' });
    } else if (isLoggedIn) {
      apiFetch('/api/grocery/clear-checked', { method: 'POST' });
    }
    setDisplayItems((prev) => prev.filter((i) => !i.completed));
    toast.info(isSpanish ? 'Artículos marcados eliminados' : 'Checked items cleared');
  };

  const allSectionOptions = [
    ...GROCERY_SECTIONS.map((s) => ({ value: s.id, label: isSpanish ? s.es : s.en })),
    ...customSections.map((c) => ({ value: c, label: c.slice(7) })),
  ];

  const acceptedLists = sharedLists.filter((l) => l.my_status === 'accepted');
  const pendingInvites = sharedLists.filter((l) => l.my_status === 'pending');
  const activeSharedMeta = isShared
    ? (sharedData?.list || acceptedLists.find((l) => l.list_id === activeList))
    : null;

  return (
    <div data-testid="grocery-list">
      {/* Pending invites */}
      {pendingInvites.map((inv) => (
        <Card key={inv.list_id} className="mb-3 border-[#7C3AED]/40 bg-gradient-to-r from-[#7C3AED]/10 to-[#00E5CC]/10" data-testid={`grocery-invite-banner-${inv.list_id}`}>
          <CardContent className="p-3 flex flex-wrap items-center gap-2">
            <Users className="h-4 w-4 text-[#7C3AED] shrink-0" />
            <p className="text-sm flex-1 min-w-[180px]">
              {isSpanish
                ? <><strong>{inv.owner_name}</strong> compartió "{inv.name}" contigo</>
                : <><strong>{inv.owner_name}</strong> shared "{inv.name}" with you</>}
            </p>
            <div className="flex gap-1.5">
              <Button size="sm" className="h-7 text-xs bg-[#00E5CC] hover:bg-[#00C9B3] text-[#0d1f2d]" onClick={() => acceptInvite(inv.list_id)} data-testid={`grocery-invite-accept-${inv.list_id}`}>
                {isSpanish ? 'Aceptar' : 'Accept'}
              </Button>
              <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => declineInvite(inv.list_id)} data-testid={`grocery-invite-decline-${inv.list_id}`}>
                {isSpanish ? 'Rechazar' : 'Decline'}
              </Button>
            </div>
          </CardContent>
        </Card>
      ))}

      {/* List switcher + share button (signed-in only) */}
      {isLoggedIn && (
        <div className="flex items-center gap-1.5 mb-3 overflow-x-auto pb-1 scrollbar-hide" data-testid="grocery-list-switcher">
          <button
            onClick={() => setActiveList('mine')}
            data-testid="grocery-list-pill-mine"
            className={`px-3 py-1 rounded-full text-xs font-medium whitespace-nowrap transition-all ${
              !isShared ? 'bg-[#00E5CC] text-[#0d1f2d] font-semibold' : 'bg-muted text-muted-foreground hover:text-foreground'
            }`}
          >
            {isSpanish ? 'Mi lista' : 'My list'}
          </button>
          {acceptedLists.map((l) => (
            <button
              key={l.list_id}
              onClick={() => setActiveList(l.list_id)}
              data-testid={`grocery-list-pill-${l.list_id}`}
              className={`px-3 py-1 rounded-full text-xs font-medium whitespace-nowrap transition-all flex items-center gap-1 ${
                activeList === l.list_id ? 'bg-[#7C3AED] text-white font-semibold' : 'bg-muted text-muted-foreground hover:text-foreground'
              }`}
            >
              <Users className="h-3 w-3" />
              {l.name} ({l.unchecked_count})
            </button>
          ))}
          <Button
            variant="outline"
            size="sm"
            className="h-6 text-xs gap-1 shrink-0 ml-auto"
            onClick={openShareDialog}
            data-testid="grocery-share-btn"
          >
            <Users className="h-3 w-3" />
            {isSpanish ? 'Compartir lista' : 'Share list'}
          </Button>
        </div>
      )}

      {/* Active shared list banner */}
      {isShared && activeSharedMeta && (
        <div className="flex flex-wrap items-center gap-2 mb-3 px-3 py-2 rounded-lg bg-[#7C3AED]/10 border border-[#7C3AED]/25 text-xs" data-testid="grocery-shared-members">
          <Users className="h-3.5 w-3.5 text-[#7C3AED] shrink-0" />
          <span className="text-muted-foreground">
            {isSpanish ? 'Compartida con:' : 'Shared with:'}
          </span>
          {(activeSharedMeta.members || []).map((m) => (
            <span key={m.user_id} className={`px-2 py-0.5 rounded-full border ${m.status === 'pending' ? 'opacity-50 border-dashed' : 'bg-background'}`}>
              {m.name}{m.status === 'pending' && ` (${isSpanish ? 'invitado' : 'invited'})`}
            </span>
          ))}
          <span className="text-[10px] text-muted-foreground ml-auto hidden sm:inline">
            {isSpanish ? 'Se actualiza en vivo' : 'Live — updates every few seconds'}
          </span>
          <Button variant="ghost" size="sm" className="h-6 text-xs gap-1 text-muted-foreground hover:text-destructive" onClick={() => leaveList(activeList)} data-testid="grocery-leave-list-btn">
            <LogOut className="h-3 w-3" />
            {isSpanish ? 'Salir' : 'Leave'}
          </Button>
        </div>
      )}

      {/* Add item form */}
      <Card className="mb-4">
        <CardContent className="p-3 sm:p-4">
          <form onSubmit={addItem} className="space-y-2">
            <div className="flex gap-2">
              <Input
                value={newText}
                onChange={(e) => setNewText(e.target.value)}
                placeholder={isSpanish ? 'Ej. leche, plátanos, pan…' : 'e.g. milk, bananas, bread…'}
                className="flex-1 min-w-0"
                data-testid="grocery-input"
              />
              <Button
                type="submit"
                disabled={!newText.trim() || (isShared && !sharedData)}
                className="bg-[#00E5CC] hover:bg-[#00C9B3] text-[#0d1f2d] font-semibold shrink-0"
                data-testid="grocery-add-btn"
              >
                <Plus className="h-4 w-4 sm:mr-1" />
                <span className="hidden sm:inline">{isSpanish ? 'Agregar' : 'Add'}</span>
              </Button>
            </div>
            <div className="flex items-center gap-2 flex-wrap">
              <Select value={sectionChoice} onValueChange={setSectionChoice}>
                <SelectTrigger className="h-8 text-xs w-auto min-w-[180px]" data-testid="grocery-section-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="auto">
                    <span className="flex items-center gap-1.5">
                      <Sparkles className="h-3 w-3 text-[#00A896]" />
                      {isSpanish ? 'Auto' : 'Auto'}
                      {newText.trim() ? ` → ${sectionLabel(suggested, isSpanish)}` : ` (${isSpanish ? 'sugerida' : 'suggested'})`}
                    </span>
                  </SelectItem>
                  {allSectionOptions.map((o) => (
                    <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
                  ))}
                  <SelectItem value="__custom__">
                    + {isSpanish ? 'Nueva sección…' : 'New section…'}
                  </SelectItem>
                </SelectContent>
              </Select>
              {sectionChoice === '__custom__' && (
                <Input
                  value={customName}
                  onChange={(e) => setCustomName(e.target.value)}
                  placeholder={isSpanish ? 'Nombre de sección…' : 'Section name…'}
                  className="h-8 text-xs flex-1 min-w-[140px]"
                  data-testid="grocery-custom-section-input"
                />
              )}
              {sectionChoice === 'auto' && newText.trim() && (
                <span className="text-[10px] text-muted-foreground flex items-center gap-1" data-testid="grocery-suggested-hint">
                  <Sparkles className="h-3 w-3 text-[#00A896]" />
                  {isSpanish ? 'Sección sugerida — cámbiala si quieres' : 'Suggested section — change it if you like'}
                </span>
              )}
            </div>
          </form>
        </CardContent>
      </Card>

      {/* List header row */}
      <div className="flex items-center justify-between mb-3">
        <p className="text-sm text-muted-foreground">
          {displayItems.length} {isSpanish ? 'artículos' : 'items'}
          {checkedCount > 0 && ` · ${checkedCount} ${isSpanish ? 'marcados' : 'checked'}`}
        </p>
        {checkedCount > 0 && (
          <Button variant="outline" size="sm" className="h-7 text-xs gap-1" onClick={clearChecked} data-testid="grocery-clear-checked">
            <Eraser className="h-3 w-3" />
            {isSpanish ? 'Quitar marcados' : 'Clear checked'}
          </Button>
        )}
      </div>

      {/* Grouped by section */}
      {isShared && !sharedData ? (
        <Card>
          <CardContent className="flex items-center justify-center py-10 text-muted-foreground">
            <Loader2 className="h-5 w-5 animate-spin mr-2" />
            {isSpanish ? 'Cargando lista compartida…' : 'Loading shared list…'}
          </CardContent>
        </Card>
      ) : displayItems.length === 0 ? (
        <Card>
          <CardContent className="text-center py-10 text-muted-foreground">
            <ShoppingCart className="h-10 w-10 mx-auto mb-3 opacity-30" />
            <p className="text-sm">
              {isSpanish
                ? 'Tu lista está vacía. Escribe un artículo y te sugerimos la sección de la tienda.'
                : 'Your list is empty. Type an item and we\'ll suggest the store section for you.'}
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-3">
          {grouped.map(({ key, items: sectionItems }) => (
            <Card key={key} data-testid={`grocery-section-${key.replace('custom:', 'custom-')}`}>
              <CardHeader className="py-2 px-4">
                <CardTitle className="text-sm flex items-center justify-between">
                  <span className="flex items-center gap-2">
                    <span className="w-2.5 h-2.5 rounded-full shrink-0" style={{ backgroundColor: sectionColor(key) }} />
                    {sectionLabel(key, isSpanish)}
                  </span>
                  <span className="text-xs font-normal text-muted-foreground">
                    {sectionItems.filter((i) => !i.completed).length}/{sectionItems.length}
                  </span>
                </CardTitle>
              </CardHeader>
              <CardContent className="pt-0 px-3 pb-2">
                <ul className="space-y-1">
                  {sectionItems.map((item) => (
                    <li
                      key={item.id}
                      className={`flex items-center gap-2 px-2 py-1.5 rounded-md transition-all ${
                        item.completed ? 'opacity-50' : 'hover:bg-muted/40'
                      }`}
                      data-testid={`grocery-item-${item.id}`}
                    >
                      <Checkbox
                        checked={item.completed}
                        onCheckedChange={() => toggleItem(item.id)}
                        className="data-[state=checked]:bg-[#00E5CC] data-[state=checked]:border-[#00E5CC] shrink-0"
                        data-testid={`grocery-check-${item.id}`}
                      />
                      {editingId === item.id ? (
                        <div className="flex-1 flex items-center gap-1 flex-wrap">
                          <Input
                            value={editText}
                            onChange={(e) => setEditText(e.target.value)}
                            onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); saveEdit(item.id); } if (e.key === 'Escape') setEditingId(null); }}
                            className="h-7 text-sm flex-1 min-w-[120px]"
                            autoFocus
                            data-testid={`grocery-edit-input-${item.id}`}
                          />
                          <Select value={editSection} onValueChange={setEditSection}>
                            <SelectTrigger className="h-7 text-xs w-auto min-w-[120px]">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              {allSectionOptions.map((o) => (
                                <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                          <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => saveEdit(item.id)} data-testid={`grocery-save-${item.id}`}>
                            <Check className="h-3 w-3 text-green-600" />
                          </Button>
                          <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => setEditingId(null)}>
                            <X className="h-3 w-3 text-red-500" />
                          </Button>
                        </div>
                      ) : (
                        <>
                          <span
                            className={`flex-1 min-w-0 text-sm cursor-pointer truncate ${item.completed ? 'line-through text-muted-foreground' : ''}`}
                            onClick={() => !item.completed && startEdit(item)}
                            data-testid={`grocery-text-${item.id}`}
                          >
                            {item.text}
                            {isShared && item.added_by_name && item.added_by !== userId && (
                              <span className="ml-1.5 text-[10px] text-muted-foreground">· {item.added_by_name}</span>
                            )}
                          </span>
                          <div className="flex items-center gap-0.5 shrink-0">
                            {!item.completed && (
                              <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => startEdit(item)} data-testid={`grocery-edit-${item.id}`}>
                                <Pencil className="h-3 w-3 text-muted-foreground" />
                              </Button>
                            )}
                            <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => deleteItem(item.id)} data-testid={`grocery-delete-${item.id}`}>
                              <Trash2 className="h-3 w-3 text-muted-foreground hover:text-destructive" />
                            </Button>
                          </div>
                        </>
                      )}
                    </li>
                  ))}
                </ul>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {displayItems.length > 0 && (
        <p className="text-center text-xs text-muted-foreground mt-3">
          {isSpanish
            ? 'Los artículos se agrupan por sección de la tienda y se ordenan alfabéticamente.'
            : 'Items are grouped by store section and alphabetized within each.'}
        </p>
      )}

      {/* Share dialog */}
      <Dialog open={shareOpen} onOpenChange={setShareOpen}>
        <DialogContent className="w-[calc(100%-2rem)] max-w-md" data-testid="grocery-share-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Users className="h-5 w-5 text-[#7C3AED]" />
              {isSpanish ? 'Compartir lista de compras' : 'Share a grocery list'}
            </DialogTitle>
            <DialogDescription>
              {isSpanish
                ? 'Crea una lista compartida con tus amigos — todos pueden agregar y marcar artículos.'
                : 'Create a shared list with your friends — everyone can add and check off items together.'}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="text-sm font-medium">{isSpanish ? 'Nombre de la lista (opcional)' : 'List name (optional)'}</Label>
              <Input
                value={shareName}
                onChange={(e) => setShareName(e.target.value)}
                placeholder={isSpanish ? 'Ej. Compras de la semana' : 'e.g. Weekly shopping'}
                className="mt-1"
                data-testid="grocery-share-name-input"
              />
            </div>
            <div>
              <Label className="text-sm font-medium">{isSpanish ? 'Elige amigos' : 'Pick friends'}</Label>
              {friends === null ? (
                <div className="flex items-center justify-center py-4 text-muted-foreground text-sm">
                  <Loader2 className="h-4 w-4 animate-spin mr-2" /> {isSpanish ? 'Cargando…' : 'Loading…'}
                </div>
              ) : friends.length === 0 ? (
                <p className="text-sm text-muted-foreground mt-1">
                  {isSpanish ? 'Aún no tienes amigos en la app. ' : 'You don\'t have any app friends yet. '}
                  <Link to="/friends" className="text-primary hover:underline" data-testid="grocery-share-friends-link">
                    {isSpanish ? 'Agrega amigos aquí →' : 'Add friends here →'}
                  </Link>
                </p>
              ) : (
                <div className="mt-1 space-y-1 max-h-52 overflow-y-auto">
                  {friends.map((f) => {
                    const fr = f.friend || {};
                    const picked = pickedFriends.has(fr.user_id);
                    return (
                      <button
                        key={fr.user_id}
                        type="button"
                        onClick={() => setPickedFriends((prev) => {
                          const next = new Set(prev);
                          if (next.has(fr.user_id)) next.delete(fr.user_id); else next.add(fr.user_id);
                          return next;
                        })}
                        data-testid={`grocery-share-friend-${fr.user_id}`}
                        aria-pressed={picked}
                        className={`w-full flex items-center gap-2 px-2.5 py-2 rounded-md border text-left text-sm transition ${
                          picked ? 'border-[#7C3AED] bg-[#7C3AED]/10' : 'border-muted hover:bg-muted/40'
                        }`}
                      >
                        <span
                          className="w-6 h-6 rounded-full flex items-center justify-center text-[10px] font-bold text-white shrink-0"
                          style={{ backgroundColor: fr.avatar_color || '#7C3AED' }}
                        >
                          {(fr.display_name || fr.name || '?').slice(0, 1).toUpperCase()}
                        </span>
                        <span className="flex-1 truncate">{fr.display_name || fr.name || fr.email}</span>
                        {picked && <Check className="h-4 w-4 text-[#7C3AED] shrink-0" />}
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
            <Button
              onClick={createSharedList}
              disabled={creating || pickedFriends.size === 0}
              className="w-full bg-[#7C3AED] hover:bg-[#6D28D9] text-white"
              data-testid="grocery-share-create-btn"
            >
              {creating
                ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> {isSpanish ? 'Creando…' : 'Creating…'}</>
                : <><Users className="h-4 w-4 mr-2" /> {isSpanish ? 'Crear lista compartida' : 'Create shared list'}</>}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default GroceryList;
